import { useState, useEffect } from 'react';
import { FileText, ImageIcon, Download, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

// Side-by-side receipt viewer for the admin expense modals. Shows a
// thumbnail/icon list on the left of the panel and the active receipt
// preview on the right. Images render inline; PDFs/other files fall
// back to a download / open-in-new-tab pair.
//
// Pair this with whatever fields panel you want — the viewer is the
// "right column" in the two-pane modal layout.

// Look at both `mimeType` (what upload-receipt actually stores) and
// `contentType` (a reasonable alias) before falling back to the file
// extension. The previous code only checked `contentType` so every
// image fell through to the unknown-file fallback tile.
function mimeOf(receipt) {
  return (receipt.mimeType || receipt.contentType || '').toLowerCase();
}

function extOf(receipt) {
  return ((receipt.fileName || receipt.blobPath || '').split('.').pop() || '').toLowerCase();
}

function isImage(receipt) {
  const m = mimeOf(receipt);
  if (m.startsWith('image/')) return true;
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'heif'].includes(extOf(receipt));
}

function isPdf(receipt) {
  const m = mimeOf(receipt);
  if (m.includes('pdf')) return true;
  return extOf(receipt) === 'pdf';
}

export default function ReceiptViewer({ receipts = [], emptyMessage = 'No receipts uploaded.', showThumbnails = true }) {
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    // Auto-select the first receipt when the list loads / changes.
    if (receipts.length > 0 && !receipts.find((r) => r.rowKey === activeId)) {
      setActiveId(receipts[0].rowKey);
    }
    if (receipts.length === 0 && activeId !== null) {
      setActiveId(null);
    }
  }, [receipts, activeId]);

  const active = receipts.find((r) => r.rowKey === activeId);

  if (receipts.length === 0) {
    return (
      <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-background/40 p-6 text-center">
        <ImageIcon className="mb-2 h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Thumbnail / file strip — only when there's more than one receipt
          to switch between. A single-item strip just wastes vertical space
          and pushed the preview off-screen in the approver's side pane. */}
      {showThumbnails && receipts.length > 1 && (
      <div className="flex flex-wrap gap-2">
        {receipts.map((r) => {
          const isActive = r.rowKey === activeId;
          return (
            <button
              key={r.rowKey}
              onClick={() => setActiveId(r.rowKey)}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors',
                isActive
                  ? 'border-primary bg-primary/10 font-medium text-foreground'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
              title={r.fileName}
            >
              {isImage(r) ? (
                <ImageIcon className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <FileText className="h-3.5 w-3.5 shrink-0" />
              )}
              <span className="max-w-[140px] truncate">{r.fileName || r.rowKey}</span>
            </button>
          );
        })}
      </div>
      )}

      {/* Preview pane — a CAPPED, definite height (not an open-ended
          min-height). A bare min-h with no ceiling let a large image
          expand the box well past the intended size and run the whole
          page/pane down — the reported "receipt pushed ridiculously far
          down." A definite height (capped, viewport-relative) means
          images object-contain to fit and PDFs scroll inside, so the box
          never grows past this. */}
      <div className="overflow-hidden rounded-lg border border-border bg-background/40 h-[60vh] max-h-[640px] min-h-[320px] flex flex-col">
        {active ? (
          <ReceiptPreview receipt={active} />
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
            Select a receipt to preview.
          </div>
        )}
      </div>
    </div>
  );
}

function ReceiptPreview({ receipt }) {
  if (!receipt.url) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
        <FileText className="h-8 w-8" />
        <span>This receipt's blob could not be loaded.</span>
        <span className="text-xs">{receipt.fileName}</span>
      </div>
    );
  }

  if (isImage(receipt)) {
    return <ImagePreview receipt={receipt} />;
  }

  if (isPdf(receipt)) {
    return (
      <div className="flex flex-1 flex-col min-h-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs">
          <span className="truncate font-medium">{receipt.fileName}</span>
          <ReceiptActions receipt={receipt} />
        </div>
        {/* Fill the remaining space inside the capped preview box and
            scroll within (min-h-0 lets the flex child shrink). No fixed
            min-height — that would overflow the cap on short viewports. */}
        <iframe
          src={receipt.url}
          title={receipt.fileName || 'Receipt PDF'}
          className="w-full flex-1 min-h-0 border-0 bg-white"
        />
      </div>
    );
  }

  // Fallback for other file types (docs, spreadsheets, etc).
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
      <FileText className="h-10 w-10 text-muted-foreground" />
      <div>
        <div className="text-sm font-medium">{receipt.fileName}</div>
        <div className="text-xs text-muted-foreground">
          {receipt.contentType || 'Preview not available for this file type'}
        </div>
      </div>
      <ReceiptActions receipt={receipt} />
    </div>
  );
}

// Image preview with explicit load/error tracking. Without this, a
// broken blob URL (404, blocked, network) made the inline <img> just
// render an invisible 0x0 element and left the user staring at an
// empty pane wondering why their image "isn't working".
function ImagePreview({ receipt }) {
  const [state, setState] = useState('loading'); // loading | loaded | error

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="flex items-center justify-between border-b border-border px-3 py-2 text-xs">
        <span className="truncate font-medium">{receipt.fileName}</span>
        <ReceiptActions receipt={receipt} />
      </div>
      {/* Scale the image to the pane WIDTH and scroll vertically for tall
          receipts — same as the expense-input preview. (Fitting to height
          instead shrank a tall/portrait receipt until the numbers were
          unreadable.) min-h-0 keeps the box itself capped on the page;
          overflow-auto scrolls a tall image inside it. items-start so the
          top of a tall receipt is visible first. */}
      <div className="relative flex flex-1 min-h-0 items-start justify-center overflow-auto bg-black/40 p-2">
        {state === 'error' && (
          <div className="flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
            <ImageIcon className="h-8 w-8" />
            <span>Image failed to load.</span>
            <span className="text-xs break-all px-4">{receipt.url || '(no URL)'}</span>
            <span className="text-[11px]">
              Likely a missing blob, expired SAS, or browser block. Use Open / Download above.
            </span>
          </div>
        )}
        {state === 'loading' && (
          <div className="absolute text-xs text-muted-foreground">Loading image…</div>
        )}
        {state !== 'error' && (
          <img
            src={receipt.url}
            alt={receipt.fileName || 'Receipt'}
            className={cn(
              'w-full h-auto object-contain rounded-md transition-opacity',
              state === 'loaded' ? 'opacity-100' : 'opacity-0'
            )}
            onLoad={() => setState('loaded')}
            onError={() => setState('error')}
          />
        )}
      </div>
    </div>
  );
}

function ReceiptActions({ receipt }) {
  if (!receipt.url) return null;
  return (
    <div className="flex items-center gap-1">
      <a
        href={receipt.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        title="Open in new tab"
      >
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
      <a
        href={receipt.url}
        download={receipt.fileName || ''}
        className="inline-flex items-center gap-1 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        title="Download"
      >
        <Download className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}
